import { OfferUnitType, Supplier } from "src/app/domains/internal";

export interface IOfferSupplierDiscountGoal {
  offer_unit_type: OfferUnitType;
  supplier_id: string;
  supplier_discount_goal_rate: number;
  comment?: string;
  cost_price: number;
  selling_price: number;
  auction_discount: number;

  marginRate: number;
  marginTotal: number;
  sellingAuction: number;
  sellingPriceAuction: number;
  discountGoalTotal: number;
  costPriceWithDiscountGoal: number;

  // sum of purchase orders
  projectedConsumed: number;
  // sum of invoices
  realConsumed: number;

  nb_hours?: number;

  // external
  supplier?: Supplier;
}

export class OfferSupplierDiscountGoal implements IOfferSupplierDiscountGoal {
  offer_unit_type: OfferUnitType;
  supplier_id: string;
  comment?: string;
  cost_price: number;
  selling_price: number;
  sellingPriceAuction: number;
  projectedConsumed: number;
  realConsumed: number;
  nb_hours: number = 0;

  // Accumulés ligne par ligne (chaque ligne peut avoir son propre taux d'objectif) plutôt
  // que recalculés depuis supplier_discount_goal_rate, qui ne reflète que la 1ère ligne
  // rencontrée pour ce fournisseur et fausse le total dès que les lignes sont hétérogènes.
  discountGoalTotal: number = 0;
  costPriceWithDiscountGoal: number = 0;

  // Taux affiché = moyenne pondérée réelle du fournisseur, cohérente avec discountGoalTotal /
  // costPriceWithDiscountGoal (cumuls ligne par ligne). On ne part plus de la 1ère ligne
  // rencontrée, qui faussait l'affichage dès que les lignes avaient des objectifs différents.
  // L'override n'est posé que lorsque l'utilisateur saisit une valeur dans le champ.
  private _supplier_discount_goal_rate_override: number | null = null;
  get supplier_discount_goal_rate(): number {
    if (this._supplier_discount_goal_rate_override != null) return this._supplier_discount_goal_rate_override;
    if (!this.sellingPriceAuction) return 0;
    return (1 - this.costPriceWithDiscountGoal / this.sellingPriceAuction) * 100;
  }
  set supplier_discount_goal_rate(val: number) {
    this._supplier_discount_goal_rate_override = val;
  }
  get isRateEdited(): boolean {
    return this._supplier_discount_goal_rate_override != null;
  }

  // Objectifs distincts rencontrés sur les lignes cumulées de ce fournisseur. Sert à n'expliquer
  // la moyenne pondérée que quand les lignes ne sont PAS toutes au même objectif (cas legacy/import).
  lineGoals: number[] = [];
  registerLineGoal(goal: number): void {
    const g = goal ?? 0;
    if (!this.lineGoals.includes(g)) this.lineGoals.push(g);
  }
  get hasMixedGoals(): boolean {
    return this.lineGoals.length > 1;
  }
  get lineGoalsLabel(): string {
    return [...this.lineGoals].sort((a, b) => a - b).join(", ");
  }

  // external
  supplier: Supplier;

  constructor(values: any) {
    Object.assign(this, values);
  }

  get marginRate(): number {
    if (!this.cost_price) return 0;
    return Math.abs((this.selling_price / this.cost_price) * 100 - 100) || 0;
  }

  get marginTotal(): number {
    return this.selling_price - this.cost_price;
  }

  get sellingAuction(): number {
    return this.selling_price - this.sellingPriceAuction;
  }

  get auction_discount(): number {
    if (!this.selling_price) return 0;
    return Math.abs((this.sellingPriceAuction / this.selling_price) * 100 - 100) || 0;
  }

  // get sellingPriceAuction(): number {
  //   return this.selling_price - this.sellingAuction;
  // }
}

export class OfferSupplierDiscountGoalStatic implements IOfferSupplierDiscountGoal {
  offer_unit_type: OfferUnitType;
  supplier_id: string;

  comment?: string;
  cost_price: number;
  get marginRate(): number {
    if (!this.cost_price) return 0;
    return Math.abs((this.selling_price / this.cost_price) * 100 - 100) || 0;
  }
  marginTotal: number;
  selling_price: number;

  get auction_discount(): number {
    if (!this.selling_price) return 0;
    return Math.abs((this.sellingPriceAuction / this.selling_price) * 100 - 100) || 0;
  }
  sellingAuction: number;
  sellingPriceAuction: number;
  nb_hours: number = 0;

  get supplier_discount_goal_rate(): number {
    if (!this.sellingPriceAuction) return 0;
    return (1 - this.costPriceWithDiscountGoal / this.sellingPriceAuction) * 100;
  }
  discountGoalTotal: number;
  costPriceWithDiscountGoal: number;
  supplier?: Supplier;
  projectedConsumed: number;
  realConsumed: number;

  constructor(values: any) {
    Object.assign(this, values);
  }
}
